import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Phone, MessageCircle, Mail, StickyNote, MapPin, AlertTriangle, TrendingUp, Bot,
  ArrowRight, Plus, Trash2, CheckCircle2, Clock, User, X, Send, Filter, ListTodo,
  BookOpen, ChevronDown, Edit3, Zap, DollarSign, Briefcase
} from "lucide-react";
import { OpportunityCreator, OpportunityEditor, OpportunityHistoryModal } from "./AnaPipelinePanel";

/**
 * V1.29 · ANA · Pestaña dentro del perfil del alumno.
 *
 * Contiene 3 sub-tabs:
 *   - Timeline 360° (eventos ANA + KAI + tareas + lifecycle stages)
 *   - Tareas (por alumno, CRUD)
 *   - Plantillas ANA (mini-preview + envío WhatsApp comercial)
 */
export default function AnaStudentTab({ student }) {
  const [tab, setTab] = useState("timeline");
  const [staleness, setStaleness] = useState(null);

  useEffect(() => {
    if (!student?.id) return;
    api.get(`/ana/students/${student.id}/contact-staleness`).then(({ data }) => setStaleness(data)).catch(() => {});
  }, [student?.id]);

  if (!student?.id) return null;

  const STALENESS_COLOR = {
    fresh: "bg-emerald-50 text-emerald-800 border-emerald-200",
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    yellow: "bg-amber-50 text-amber-800 border-amber-200",
    orange: "bg-orange-50 text-orange-800 border-orange-200",
    red: "bg-red-50 text-red-800 border-red-200",
    never: "bg-neutral-50 text-neutral-600 border-neutral-200",
  };

  return (
    <div className="space-y-3" data-testid="ana-student-tab">
      {staleness && (
        <div data-testid="ana-staleness-badge" className={"inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold " + (STALENESS_COLOR[staleness.bucket] || STALENESS_COLOR.never)}>
          {staleness.bucket === "fresh" || staleness.bucket === "green" ? "🟢" :
            staleness.bucket === "yellow" ? "🟡" :
            staleness.bucket === "orange" ? "🟠" :
            staleness.bucket === "red" ? "🔴" : "⚪"} {staleness.label}
        </div>
      )}
      <div className="flex items-center gap-1 border-b border-neutral-200">
        {[
          { key: "timeline",       label: "Timeline 360°", icon: TrendingUp },
          { key: "tasks",          label: "Tareas",        icon: ListTodo },
          { key: "opportunities",  label: "Oportunidades", icon: Briefcase },
          { key: "templates",      label: "Plantillas",    icon: BookOpen },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`ana-subtab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={
                "inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-all " +
                (active ? "text-orange-700 border-orange-500" : "text-neutral-500 border-transparent hover:text-neutral-800")
              }
            >
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "timeline" && <AnaTimelineSection student={student} />}
      {tab === "tasks" && <AnaTasksSection student={student} />}
      {tab === "opportunities" && <AnaOpportunitiesSection student={student} />}
      {tab === "templates" && <AnaTemplatesQuickSection student={student} />}
    </div>
  );
}


// ============================================================
// TIMELINE 360°
// ============================================================
const EVENT_ICONS = {
  call: Phone, whatsapp: MessageCircle, email: Mail, note: StickyNote,
  visit: MapPin, incident: AlertTriangle, sale: TrendingUp,
  kai_send: Bot, task_done: CheckCircle2, task_cancelled: X, stage_change: ArrowRight,
};
const EVENT_COLORS = {
  call: "bg-blue-100 text-blue-700",
  whatsapp: "bg-emerald-100 text-emerald-700",
  email: "bg-purple-100 text-purple-700",
  note: "bg-neutral-100 text-neutral-700",
  visit: "bg-amber-100 text-amber-700",
  incident: "bg-red-100 text-red-700",
  sale: "bg-lime-100 text-lime-800",
  kai_send: "bg-orange-100 text-orange-700",
  task_done: "bg-green-100 text-green-700",
  task_cancelled: "bg-neutral-200 text-neutral-500",
  stage_change: "bg-sky-100 text-sky-800",
};

function AnaTimelineSection({ student }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [creatorOpen, setCreatorOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ana/students/${student.id}/timeline`);
      setEvents(data.events || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [student.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return events;
    if (filter === "manual") return events.filter((e) => e.source === "ana");
    if (filter === "kai") return events.filter((e) => e.source === "kai");
    if (filter === "tasks") return events.filter((e) => e.source === "task");
    if (filter === "lifecycle") return events.filter((e) => e.source === "lifecycle");
    return events;
  }, [events, filter]);

  const handleDelete = async (evId) => {
    if (!window.confirm("¿Borrar este evento del timeline?")) return;
    try {
      await api.delete(`/ana/timeline/${evId}`);
      toast.success("Evento eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3" data-testid="ana-timeline-section">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 text-xs font-semibold text-neutral-600">
          <Filter className="h-3.5 w-3.5" /> Fuente:
        </div>
        {[
          { key: "all", label: "Todo" },
          { key: "manual", label: "Manual (Ana)" },
          { key: "kai", label: "KAI (auto)" },
          { key: "tasks", label: "Tareas" },
          { key: "lifecycle", label: "Estados" },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            data-testid={`ana-timeline-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={
              "text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors " +
              (filter === f.key
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white text-neutral-600 border-neutral-300 hover:border-neutral-400")
            }
          >
            {f.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setCreatorOpen(true)}
          data-testid="ana-timeline-create-btn"
          className="ml-auto btn-primary"
        >
          <Plus className="h-3.5 w-3.5" /> Registrar
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-4">Cargando timeline…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">
          Sin eventos que mostrar todavía. Registra la primera llamada o WhatsApp con el botón "Registrar".
        </div>
      ) : (
        <div className="relative pl-6 space-y-2" data-testid="ana-timeline-list">
          <div className="absolute left-2.5 top-2 bottom-2 w-px bg-neutral-200" aria-hidden />
          {filtered.map((e) => {
            const Icon = EVENT_ICONS[e.type] || StickyNote;
            const colorCls = EVENT_COLORS[e.type] || "bg-neutral-100 text-neutral-700";
            return (
              <div key={e.id} className="relative" data-testid={`ana-timeline-event-${e.id}`}>
                <div className={"absolute -left-6 top-2 h-5 w-5 rounded-full flex items-center justify-center " + colorCls}>
                  <Icon className="h-3 w-3" />
                </div>
                <div className="rounded-lg border border-neutral-200 bg-white p-2.5">
                  <div className="flex items-start justify-between gap-2 mb-0.5">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-neutral-800">
                        {e.title}
                        {e.outcome_label && (
                          <span className="ml-1.5 text-[10px] font-semibold rounded-full bg-neutral-100 text-neutral-700 px-1.5 py-0.5">{e.outcome_label}</span>
                        )}
                        {e.meta?.was_edited && (
                          <span className="ml-1.5 text-[10px] font-semibold text-orange-700">· editado</span>
                        )}
                      </div>
                      <div className="text-[10px] text-neutral-500 flex items-center gap-2 mt-0.5">
                        <span>{new Date(e.ts).toLocaleString("es-ES")}</span>
                        {e.author && <><span>·</span><span>{e.author}</span></>}
                        {e.direction && e.direction !== "internal" && (
                          <><span>·</span><span className="uppercase">{e.direction === "in" ? "Entrante" : "Saliente"}</span></>
                        )}
                        <span>·</span>
                        <span className="uppercase text-neutral-400">{e.source}</span>
                      </div>
                    </div>
                    {e.can_delete && (
                      <button
                        onClick={() => handleDelete(e.id)}
                        className="text-neutral-400 hover:text-red-600 flex-shrink-0"
                        title="Borrar evento"
                        data-testid={`ana-timeline-delete-${e.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {e.body && (
                    <div className="text-xs text-neutral-700 whitespace-pre-wrap mt-1">{e.body}</div>
                  )}
                  {e.meta?.wa_url && (
                    <a href={e.meta.wa_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1 mt-1">
                      Ver en WhatsApp →
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creatorOpen && (
        <AnaTimelineCreator
          student={student}
          onClose={() => setCreatorOpen(false)}
          onCreated={() => { setCreatorOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function AnaTimelineCreator({ student, onClose, onCreated }) {
  const [catalog, setCatalog] = useState({ call_outcomes: [] });
  const [type, setType] = useState("call");
  const [outcome, setOutcome] = useState("");
  const [direction, setDirection] = useState("out");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/ana/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (type !== "call") setOutcome("");
  }, [type]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/ana/students/${student.id}/timeline`, {
        type, outcome: type === "call" ? (outcome || null) : null,
        direction, body,
      });
      toast.success("Evento registrado en el timeline");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const TYPES = [
    { key: "call", label: "📞 Llamada" },
    { key: "whatsapp", label: "💬 WhatsApp" },
    { key: "email", label: "📧 Email" },
    { key: "note", label: "📝 Nota" },
    { key: "visit", label: "🏢 Visita" },
    { key: "incident", label: "⚠️ Incidencia" },
    { key: "sale", label: "💰 Venta" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <h3 className="font-semibold text-lg">Nuevo evento comercial</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-neutral-700 mb-1 block">Tipo</label>
            <div className="grid grid-cols-4 gap-1.5">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`ana-creator-type-${t.key}`}
                  onClick={() => setType(t.key)}
                  className={
                    "text-[11px] font-semibold rounded-lg px-1.5 py-2 border transition-colors " +
                    (type === t.key
                      ? "bg-orange-100 border-orange-500 text-orange-800"
                      : "bg-white border-neutral-300 text-neutral-700 hover:border-neutral-400")
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {type === "call" && (
            <div>
              <label className="text-xs font-semibold text-neutral-700 mb-1 block">Resultado de la llamada</label>
              <select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                data-testid="ana-creator-outcome"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">— Selecciona —</option>
                {(catalog.call_outcomes || []).map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          {(type === "call" || type === "whatsapp" || type === "email" || type === "visit") && (
            <div>
              <label className="text-xs font-semibold text-neutral-700 mb-1 block">Dirección</label>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setDirection("out")}
                  className={"text-xs px-3 py-1.5 rounded-lg border " + (direction === "out" ? "bg-orange-100 border-orange-500 text-orange-800" : "bg-white border-neutral-300")}
                >Saliente</button>
                <button
                  type="button"
                  onClick={() => setDirection("in")}
                  className={"text-xs px-3 py-1.5 rounded-lg border " + (direction === "in" ? "bg-orange-100 border-orange-500 text-orange-800" : "bg-white border-neutral-300")}
                >Entrante</button>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-neutral-700 mb-1 block">Notas / Detalle</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              data-testid="ana-creator-body"
              className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Detalles del contacto, próximos pasos, observaciones..."
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 bg-neutral-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="ana-creator-save">
            <Send className="h-4 w-4" /> {saving ? "Guardando…" : "Registrar evento"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// TAREAS
// ============================================================
const PRIORITY_STYLE = {
  low: "text-neutral-500",
  normal: "text-blue-600",
  high: "text-amber-700",
  urgent: "text-red-700",
};
const PRIORITY_LABEL = { low: "Baja", normal: "Normal", high: "Alta", urgent: "Urgente" };
const STATUS_STYLE = {
  pending: "bg-neutral-100 text-neutral-700",
  in_progress: "bg-blue-100 text-blue-800",
  done: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};
const STATUS_LABEL = { pending: "Pendiente", in_progress: "En curso", done: "Completada", cancelled: "Cancelada" };
const TYPE_LABEL = { call: "📞 Llamar", whatsapp: "💬 WhatsApp", email: "📧 Email", followup: "🔁 Seguimiento", docs: "📄 Docs", sale: "💰 Venta", custom: "✏️ Custom" };

function AnaTasksSection({ student }) {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: t }, { data: u }] = await Promise.all([
        api.get(`/ana/students/${student.id}/tasks`),
        api.get(`/ana/users`),
      ]);
      setTasks(t.tasks || []);
      setUsers(u.users || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  }, [student.id]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => tasks.filter((t) => showCompleted ? true : (t.status === "pending" || t.status === "in_progress")), [tasks, showCompleted]);

  const complete = async (t) => {
    try {
      await api.post(`/ana/tasks/${t.id}/complete`);
      toast.success("Tarea completada");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const cancel = async (t) => {
    if (!window.confirm(`¿Cancelar la tarea "${t.title}"?`)) return;
    try {
      await api.patch(`/ana/tasks/${t.id}`, { status: "cancelled" });
      toast.success("Tarea cancelada");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm("¿Borrar esta tarea permanentemente?")) return;
    try {
      await api.delete(`/ana/tasks/${t.id}`);
      toast.success("Tarea eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3" data-testid="ana-tasks-section">
      <div className="flex items-center gap-2">
        <div className="text-xs font-semibold text-neutral-600">
          {visible.length} tarea{visible.length === 1 ? "" : "s"} {showCompleted ? "en total" : "activas"}
        </div>
        <label className="text-xs text-neutral-600 inline-flex items-center gap-1.5 ml-2">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            data-testid="ana-tasks-show-completed"
          />
          Ver completadas / canceladas
        </label>
        <button
          type="button"
          onClick={() => setCreatorOpen(true)}
          data-testid="ana-tasks-create-btn"
          className="ml-auto btn-primary"
        >
          <Plus className="h-3.5 w-3.5" /> Nueva tarea
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">
          {showCompleted ? "Sin tareas." : "No hay tareas activas. ¡Buen trabajo!"}
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="ana-tasks-list">
          {visible.map((t) => (
            <div key={t.id} data-testid={`ana-task-${t.id}`} className="rounded-lg border border-neutral-200 bg-white p-2.5">
              <div className="flex items-center gap-2">
                <div className={"text-[10px] font-semibold rounded-full px-1.5 py-0.5 flex-shrink-0 " + STATUS_STYLE[t.status]}>
                  {STATUS_LABEL[t.status]}
                </div>
                <div className="text-xs font-medium text-neutral-700 flex-shrink-0">
                  {TYPE_LABEL[t.type] || t.type}
                </div>
                <div className={"text-[10px] font-semibold flex-shrink-0 " + PRIORITY_STYLE[t.priority]}>
                  {PRIORITY_LABEL[t.priority]}
                </div>
                <div className="min-w-0 flex-1 text-sm font-semibold text-neutral-800 truncate">
                  {t.title}
                </div>
                {(t.status === "pending" || t.status === "in_progress") && (
                  <>
                    <button
                      onClick={() => complete(t)}
                      data-testid={`ana-task-complete-${t.id}`}
                      className="text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 rounded px-1.5 py-1 inline-flex items-center gap-1"
                      title="Marcar completada"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Hecha
                    </button>
                    <button
                      onClick={() => cancel(t)}
                      className="text-[11px] font-semibold text-neutral-500 hover:text-neutral-800 rounded px-1.5 py-1"
                      title="Cancelar"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => remove(t)}
                  className="text-neutral-400 hover:text-red-600"
                  title="Borrar"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                {t.due_at && (
                  <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{new Date(t.due_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                )}
                {t.assignee_email && (
                  <span className="inline-flex items-center gap-0.5"><User className="h-3 w-3" />{t.assignee_email}</span>
                )}
              </div>
              {t.notes && (
                <div className="text-xs text-neutral-700 mt-1 whitespace-pre-wrap">{t.notes}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {creatorOpen && (
        <AnaTaskCreator
          student={student}
          users={users}
          onClose={() => setCreatorOpen(false)}
          onCreated={() => { setCreatorOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function AnaTaskCreator({ student, users, onClose, onCreated }) {
  const [type, setType] = useState("call");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [priority, setPriority] = useState("normal");
  const [assignee, setAssignee] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error("Título obligatorio"); return; }
    setSaving(true);
    try {
      await api.post("/ana/tasks", {
        student_id: student.id, type, title, notes,
        priority, assignee_email: assignee || null,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
      });
      toast.success("Tarea creada");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <h3 className="font-semibold text-lg">Nueva tarea</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-neutral-700 mb-1 block">Tipo</label>
            <div className="grid grid-cols-4 gap-1.5">
              {Object.entries(TYPE_LABEL).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setType(k)}
                  data-testid={`ana-task-creator-type-${k}`}
                  className={
                    "text-[11px] font-semibold rounded-lg px-1 py-1.5 border transition-colors " +
                    (type === k
                      ? "bg-orange-100 border-orange-500 text-orange-800"
                      : "bg-white border-neutral-300 text-neutral-700 hover:border-neutral-400")
                  }
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700 mb-1 block">Título</label>
            <input
              type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="ana-task-creator-title"
              placeholder="p.ej. Llamar mañana para confirmar horario"
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-neutral-700 mb-1 block">Fecha límite</label>
              <input
                type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)}
                data-testid="ana-task-creator-due"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700 mb-1 block">Prioridad</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="ana-task-creator-priority">
                <option value="low">Baja</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700 mb-1 block">Responsable</label>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="ana-task-creator-assignee">
              <option value="">Yo mismo</option>
              {users.map((u) => (
                <option key={u.id || u.email} value={u.email}>{u.name || u.email} ({u.email})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-700 mb-1 block">Notas</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              data-testid="ana-task-creator-notes"
              className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 bg-neutral-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="ana-task-creator-save">
            {saving ? "Guardando…" : "Crear tarea"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// OPORTUNIDADES DEL ALUMNO (V1.30)
// ============================================================
const OPP_STAGE_COLORS = {
  nueva: "bg-neutral-100 text-neutral-700",
  oferta_enviada: "bg-blue-100 text-blue-700",
  pendiente_respuesta: "bg-amber-100 text-amber-700",
  interesado: "bg-purple-100 text-purple-700",
  confirmada: "bg-sky-100 text-sky-700",
  pagada: "bg-emerald-100 text-emerald-700",
  planificada: "bg-cyan-100 text-cyan-700",
  finalizada: "bg-lime-100 text-lime-700",
  no_interesado: "bg-red-100 text-red-700",
};

function AnaOpportunitiesSection({ student }) {
  const [opps, setOpps] = useState([]);
  const [catalog, setCatalog] = useState({ stages: [], products: [] });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [editorOpp, setEditorOpp] = useState(null);
  const [historyOpp, setHistoryOpp] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c, u] = await Promise.all([
        api.get(`/ana/students/${student.id}/opportunities`),
        api.get("/ana/pipeline/catalog"),
        api.get("/ana/users"),
      ]);
      setOpps(o.data.opportunities || []);
      setCatalog(c.data || {});
      setUsers(u.data.users || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, [student.id]);
  useEffect(() => { load(); }, [load]);

  const moveStage = async (opp, toStage) => {
    if (opp.stage === toStage) return;
    if (toStage === "no_interesado" || toStage === "pagada") {
      // requiere modal — abrimos editor con la oportunidad y avisamos
      toast("Cambia a este estado desde el Pipeline global para introducir importe/nota.", {
        description: "Usa Dashboard → Comercial · ANA → Pipeline.",
      });
      return;
    }
    try {
      const { data } = await api.post(`/ana/opportunities/${opp.id}/move-stage`, { to: toStage });
      toast.success(`Movida a "${data.opportunity.stage}"`);
      if (data.lifecycle_suggestion) {
        const sug = data.lifecycle_suggestion;
        toast(sug.message, {
          duration: 12000,
          action: {
            label: "Sí, aplicar",
            onClick: async () => {
              try {
                await api.patch(`/students/${sug.student_id}/lifecycle-stage`, {
                  lifecycle_stage: sug.suggested_lifecycle_stage,
                  note: `Actualizado desde pipeline ANA (V1.30)`,
                });
                toast.success("Estado del alumno actualizado");
              } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
            },
          },
        });
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const totalOpen = opps.filter((o) => !["pagada", "finalizada", "no_interesado"].includes(o.stage)).reduce((s, o) => s + (o.amount || 0), 0);
  const totalCobrado = opps.filter((o) => o.stage === "pagada").reduce((s, o) => s + (o.actual_amount || o.amount || 0), 0);

  return (
    <div className="space-y-3" data-testid="ana-opportunities-section">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> Pipeline abierto: <strong>{totalOpen.toLocaleString("es-ES")} €</strong></span>
          <span className="inline-flex items-center gap-1 text-emerald-700"><DollarSign className="h-3.5 w-3.5" /> Cobrado: <strong>{totalCobrado.toLocaleString("es-ES")} €</strong></span>
        </div>
        <button onClick={() => setCreatorOpen(true)} data-testid="ana-opps-student-create" className="ml-auto btn-primary">
          <Plus className="h-3.5 w-3.5" /> Nueva oportunidad
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-4">Cargando…</div>
      ) : opps.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500" data-testid="ana-opps-student-empty">
          Sin oportunidades registradas para este alumno.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="ana-opps-student-list">
          {opps.map((o) => (
            <div key={o.id} data-testid={`ana-opp-student-${o.id}`} className="rounded-lg border border-neutral-200 bg-white p-2.5">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <button type="button" onClick={() => setEditorOpp({ ...o, student: { id: student.id, name: student.name } })} className="text-left w-full">
                    <div className="text-sm font-semibold text-neutral-800 truncate">{o.title}</div>
                    <div className="text-[10px] text-neutral-500">{o.product_slug} · v{o.probability}%</div>
                  </button>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-semibold">{(o.stage === "pagada" && o.actual_amount != null ? o.actual_amount : o.amount || 0).toLocaleString("es-ES")} €</div>
                  <span className={"text-[9px] font-semibold rounded-full px-1.5 py-0.5 " + (OPP_STAGE_COLORS[o.stage] || "bg-neutral-100 text-neutral-700")}>{o.stage}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={o.stage}
                  onChange={(e) => moveStage(o, e.target.value)}
                  data-testid={`ana-opp-student-stage-${o.id}`}
                  className="text-[11px] rounded border border-neutral-200 px-1.5 py-0.5 bg-white"
                >
                  {catalog.stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <button onClick={() => setHistoryOpp(o)} className="text-[11px] font-semibold text-neutral-500 hover:text-blue-700 inline-flex items-center gap-1">Historial</button>
                <span className="text-[10px] text-neutral-500 ml-auto">{o.owner_email}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {creatorOpen && (
        <OpportunityCreator
          catalog={catalog}
          users={users}
          defaultStudentId={student.id}
          onClose={() => setCreatorOpen(false)}
          onCreated={() => { setCreatorOpen(false); load(); }}
        />
      )}
      {editorOpp && (
        <OpportunityEditor
          opp={editorOpp}
          catalog={catalog}
          users={users}
          onClose={() => setEditorOpp(null)}
          onSaved={() => { setEditorOpp(null); load(); }}
        />
      )}
      {historyOpp && (
        <OpportunityHistoryModal opp={historyOpp} onClose={() => setHistoryOpp(null)} />
      )}
    </div>
  );
}


// ============================================================
// PLANTILLAS ANA (preview + envío desde ficha alumno)
// ============================================================
function AnaTemplatesQuickSection({ student }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [previewText, setPreviewText] = useState("");
  const [unknownVars, setUnknownVars] = useState([]);
  const [editing, setEditing] = useState(false);
  const [customBody, setCustomBody] = useState("");
  const [category, setCategory] = useState("all");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/ana/templates?active=true").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => t.category && set.add(t.category));
    return Array.from(set).sort();
  }, [templates]);

  const filtered = useMemo(() => templates.filter((t) => category === "all" || t.category === category), [templates, category]);
  const selected = useMemo(() => templates.find((t) => t.id === selectedId), [templates, selectedId]);

  useEffect(() => {
    if (!selected) { setPreviewText(""); setUnknownVars([]); return; }
    const payload = editing ? { body: customBody } : { template_id: selected.id };
    api.post(`/ana/students/${student.id}/preview`, payload).then(({ data }) => {
      setPreviewText(data.rendered || "");
      setUnknownVars(data.unknown_variables || []);
    }).catch(() => {});
  }, [selected, student.id, editing, customBody]);

  const handleSelect = (t) => {
    setSelectedId(t.id);
    setEditing(false);
    setCustomBody(t.body || "");
  };

  const sendWhatsApp = async () => {
    if (!student?.phone) { toast.error("El alumno no tiene teléfono"); return; }
    setSending(true);
    try {
      // Registramos el evento en el timeline ANA (fuente comercial, NO se
      // duplica en KAI). Guardamos el texto renderizado enviado.
      const bodyToSend = editing ? customBody : selected.body;
      const previewRes = await api.post(`/ana/students/${student.id}/preview`, { body: bodyToSend });
      const rendered = previewRes.data.rendered || "";
      // wa.me link
      const raw = (student.phone || "").replace(/[^0-9]/g, "");
      const waPhone = raw.startsWith("34") ? raw : (raw.length === 9 ? "34" + raw : raw);
      const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(rendered)}`;
      // Registra en timeline
      await api.post(`/ana/students/${student.id}/timeline`, {
        type: "whatsapp", direction: "out", body: rendered,
        meta: { template_slug: selected.slug, template_version: selected.version, was_edited: editing, wa_url: url },
      });
      window.open(url, "_blank", "noopener");
      toast.success("WhatsApp abierto y registrado en el timeline");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-3" data-testid="ana-templates-quick">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-xs font-semibold text-neutral-600">
          <Filter className="h-3.5 w-3.5" /> Categoría:
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="ana-templates-category"
          className="rounded-full border border-neutral-300 bg-white px-3 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="all">Todas</option>
          {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="ml-auto text-xs text-neutral-500">{filtered.length} plantilla{filtered.length === 1 ? "" : "s"}</span>
      </div>

      <div className="grid gap-3 lg:grid-cols-5">
        <div className="lg:col-span-2 space-y-1.5 max-h-[380px] overflow-y-auto pr-1" data-testid="ana-templates-list">
          {filtered.map((t) => (
            <button
              key={t.id}
              onClick={() => handleSelect(t)}
              data-testid={`ana-template-${t.slug}`}
              className={
                "w-full text-left rounded-lg border px-2.5 py-2 transition-all " +
                (selectedId === t.id
                  ? "border-orange-500 bg-orange-50 ring-1 ring-orange-200"
                  : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50")
              }
            >
              <div className="text-sm font-semibold truncate">{t.title}</div>
              <div className="text-[10px] text-neutral-500">{t.category} · v{t.version}</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-3 space-y-2">
          {!selected ? (
            <div className="rounded-lg border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">
              ← Selecciona una plantilla comercial
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                <div className="flex items-center justify-between bg-neutral-50 border-b border-neutral-200 px-3 py-2">
                  <div className="text-xs font-semibold text-neutral-700 truncate">
                    {selected.title}
                  </div>
                  {!editing ? (
                    <button onClick={() => { setEditing(true); setCustomBody(selected.body || ""); }} className="text-[11px] font-semibold text-orange-700 hover:text-orange-900 inline-flex items-center gap-1" data-testid="ana-template-edit-inline">
                      <Edit3 className="h-3 w-3" /> Editar
                    </button>
                  ) : (
                    <button onClick={() => { setEditing(false); setCustomBody(selected.body || ""); }} className="text-[11px] font-semibold text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1">
                      <X className="h-3 w-3" /> Cancelar
                    </button>
                  )}
                </div>
                {editing && (
                  <textarea value={customBody} onChange={(e) => setCustomBody(e.target.value)} className="w-full min-h-[110px] p-3 text-sm font-mono focus:outline-none border-b border-neutral-200" data-testid="ana-template-inline-editor" />
                )}
                <div className="p-3 text-sm whitespace-pre-wrap text-neutral-900" data-testid="ana-template-preview">
                  {previewText || <span className="italic text-neutral-400">(sin contenido)</span>}
                </div>
                {unknownVars.length > 0 && (
                  <div className="border-t border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-800">
                    ⚠ Variables desconocidas: {unknownVars.map((v) => `{{${v}}}`).join(", ")}
                  </div>
                )}
              </div>
              <button
                onClick={sendWhatsApp}
                disabled={sending || !student?.phone}
                data-testid="ana-template-send-whatsapp"
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" /> {sending ? "Enviando…" : "Enviar WhatsApp comercial"}
              </button>
              {!student?.phone && (
                <span className="text-[11px] text-red-700 ml-2">Sin teléfono registrado</span>
              )}
              <div className="text-[10px] text-neutral-500">
                <Zap className="h-3 w-3 inline mr-0.5 text-orange-500" />
                Este envío se registrará en el <strong>Timeline ANA</strong> (comercial) — no en el historial KAI.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
